/**
 * MockDetector Smart Whitelisting System
 * 
 * Purpose: Eliminate false positives by intelligently filtering out:
 * - MockDetector's own code
 * - Legitimate configuration files
 * - Supabase publishable keys (MUST be in code)
 * - Database queries and API patterns
 * - Explicit "no mock data" declarations
 */

export interface WhitelistConfig {
  files: RegExp[];
  codePatterns: RegExp[];
  jsonKeys: string[];
  contextKeywords: string[];
}

export const WHITELIST_PATTERNS: WhitelistConfig = {
  // Files that should never be scanned
  files: [
    /scanPatterns\.ts$/,              // Pattern definitions themselves
    /@mockdetector\/widget/,          // Package namespace (widget files only)
    /src\/utils\/confidenceScoring\.ts$/,  // Explicit internal files
    /src\/utils\/contextualAutoFix\.ts$/,
    /src\/utils\/violationScoring\.ts$/,
    /src\/utils\/scanPatterns\.ts$/,
    /src\/utils\/databasePatterns\.ts$/,
    /src\/utils\/reactPatterns\.ts$/,
    /src\/utils\/smartWhitelist\.ts$/,
    /src\/hooks\/useClientSourceScanner\.ts$/,
    /src\/hooks\/useDatabaseScanner\.ts$/,
    /src\/hooks\/useMockDetection\.ts$/,
    /src\/components\/MockDetectorFloatingBubble\.tsx$/,
    /src\/components\/MockDetectorWidget\.tsx$/,
    /\.config\.(ts|js)$/,             // Config files (vite, tailwind, etc)
    /tailwind\.config/,
    /vite\.config/,
    /\.types\.ts$/,                   // Type definition files
  ],
  
  // Code patterns that are legitimate (not mock data)
  codePatterns: [
    // Supabase patterns (publishable keys MUST be in code)
    /VITE_SUPABASE_PUBLISHABLE_KEY/,
    /VITE_SUPABASE_ANON_KEY/,
    /VITE_SUPABASE_URL/,
    /SUPABASE_PUBLISHABLE_KEY.*=.*["']eyJ/,  // JWT-style anon key
    
    // Database query patterns (legitimate data access)
    /\.from\(['"][\w_]+['"]\)/,       // supabase.from('table')
    /\.select\(['"*'"]?\)/,            // .select() or .select('*')
    /\.insert\(/,                      // .insert() queries
    /\.update\(/,                      // .update() queries
    /\.limit\(\d+\)/,                  // Pagination
    /\.order\(/,                       // Sorting
    /\.eq\(['"][\w_]+['"]/,           // Where clauses
    
    // Authentication patterns
    /auth\.getUser\(\)/,
    /auth\.getSession\(\)/,
    /auth\.signIn/,
    /auth\.signOut/,
    
    // Import statements (not data)
    /^import\s+/,
    /^export\s+/,
    
    // Type definitions (not data)
    /^interface\s+/,
    /^type\s+\w+\s*=/,
    
    // Comments explaining "no mock data" policy
    /CRITICAL.*REAL.*AUTHENTIC/,
    /never\s+demo\/fake\s+data/i,
    /authentic.*systems.*from.*day.*one/i,
  ],
  
  // JSON keys that indicate "no mock data" policy
  jsonKeys: [
    'mock_data_rejected',
    'authentic_only_policy',
    'zero_mock_data',
    'authenticity_level',
    'real_apis_contacted',
    'platform_compliance',
    'authenticity_score',
    'no_demo_data',
    'production_ready',
    'real_data_only',
    'authentic_communication',
    'legal_compliance',
  ],
  
  // Context keywords that indicate legitimate content
  contextKeywords: [
    'MockDetector',                   // Our own UI text
    'Quick Scan',
    'Database Scan',
    'Scan All Routes',
    'Smart Deep Scan',
    'Total Issues',
    'Scanned:',
    'Generated by MockDetector',
    'Mock Data Violations',
    'Scan Duration',
    'Role Visibility',
    'Credits',
    'Purchase',
  ]
};

// ============= PERFORMANCE CACHING =============

/**
 * Cache for file whitelist checks to avoid repeated regex tests
 */
const fileWhitelistCache = new Map<string, boolean>();

/**
 * Cache for code pattern whitelist checks
 */
const codePatternCache = new Map<string, boolean>();

/**
 * Clear caches (useful for testing or memory management)
 */
export function clearWhitelistCaches(): void {
  fileWhitelistCache.clear();
  codePatternCache.clear();
}

/**
 * Check if a file should be whitelisted (never scanned) - WITH CACHING
 */
export function isWhitelistedFile(filePath?: string): boolean {
  if (!filePath) return false;
  
  // Check cache first
  if (fileWhitelistCache.has(filePath)) {
    return fileWhitelistCache.get(filePath)!;
  }
  
  // Compute and cache result
  const isWhitelisted = WHITELIST_PATTERNS.files.some(pattern => pattern.test(filePath));
  fileWhitelistCache.set(filePath, isWhitelisted);
  return isWhitelisted;
}

/**
 * Check if a code line contains whitelisted patterns - WITH CACHING
 */
export function isWhitelistedCode(line: string): boolean {
  // Only cache for exact strings (not regex patterns)
  // This is mainly useful for repeated checks on same lines
  if (codePatternCache.has(line)) {
    return codePatternCache.get(line)!;
  }
  
  const isWhitelisted = WHITELIST_PATTERNS.codePatterns.some(pattern => pattern.test(line));
  
  // Only cache if cache size is reasonable (prevent memory bloat)
  if (codePatternCache.size < 1000) {
    codePatternCache.set(line, isWhitelisted);
  }
  
  return isWhitelisted;
}

/**
 * Check if text contains MockDetector's own UI keywords
 */
export function isWhitelistedContext(text: string): boolean {
  return WHITELIST_PATTERNS.contextKeywords.some(keyword => text.includes(keyword));
}

/**
 * Check if JSON contains explicit "no mock data" declarations
 */
export function hasNoMockDataDeclaration(json: any): boolean {
  if (!json || typeof json !== 'object') return false;
  
  const jsonStr = JSON.stringify(json).toLowerCase();
  
  // Check for explicit declarations
  return WHITELIST_PATTERNS.jsonKeys.some(key => 
    jsonStr.includes(key.toLowerCase())
  );
}

/**
 * Check if JSON is legitimate API response metadata
 */
export function isApiResponseMetadata(json: any): boolean {
  if (!json || typeof json !== 'object') return false;
  
  const jsonStr = JSON.stringify(json).toLowerCase();
  
  // Common API response indicators
  const apiIndicators = [
    'api.github.com',
    'api.stackexchange.com',
    'api.openai.com',
    'googleapis.com',
    'real_ais_found',
    'api_response_valid',
    'api_metadata',
    'response_headers',
    'rate_limit',
    'x-ratelimit',
  ];
  
  return apiIndicators.some(indicator => jsonStr.includes(indicator));
}

/**
 * Check if JSON is configuration data (features, amenities, settings)
 */
export function isConfigurationData(json: any): boolean {
  if (!json || typeof json !== 'object') return false;
  
  const jsonStr = JSON.stringify(json).toLowerCase();
  
  // Configuration indicators
  const configIndicators = [
    '_features',
    '_amenities',
    '_config',
    '_settings',
    '_capabilities',
    '_permissions',
    'room_amenities',
    'wellness_benefits',
    'sensory_config',
    'personality_traits',
  ];
  
  return configIndicators.some(indicator => jsonStr.includes(indicator));
}

/**
 * Check if file content contains MockDetector pattern DEFINITIONS (not usage)
 */
export function isPatternDefinitionFile(content: string): boolean {
  const indicators = [
    'export const MOCK_DATA_PATTERNS',
    'export const REACT_MOCK_PATTERNS',
    'export const DATABASE_MOCK_PATTERNS',
    'calculateViolationConfidence',
    'enhanceViolationWithFix',
    'interface MockPattern',
    'getSeverityScore',
    'getPriorityScore'
  ];
  
  // If file contains 3+ pattern definition indicators, it's MockDetector internal
  const matches = indicators.filter(indicator => content.includes(indicator)).length;
  return matches >= 3;
}

/**
 * Master whitelist check - returns true if content should be SKIPPED
 */
export function shouldSkipScanning(
  content: string,
  filePath?: string,
  additionalContext?: any
): boolean {
  // FIRST: Check if it's a pattern definition file
  if (isPatternDefinitionFile(content)) {
    console.log(`⚪ Whitelisted: Pattern definition file detected`);
    return true;
  }
  
  // 1. Check file whitelist
  if (isWhitelistedFile(filePath)) {
    return true;
  }
  
  // 2. Check code patterns
  if (isWhitelistedCode(content)) {
    return true;
  }
  
  // 3. Check context keywords
  if (isWhitelistedContext(content)) {
    return true;
  }
  
  // 4. Check JSON-specific whitelists if applicable
  if (additionalContext && typeof additionalContext === 'object') {
    if (hasNoMockDataDeclaration(additionalContext) ||
        isApiResponseMetadata(additionalContext) ||
        isConfigurationData(additionalContext)) {
      return true;
    }
  }
  
  return false;
}
